import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Employee, InvitationResult, RoleSummary } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { InvitationLink } from '../../components/InvitationLink';
import { useSession } from '../../session';
import { EmployeeFiles } from './EmployeeFiles';
import { EmployeeHistory } from './EmployeeHistory';
import { EmployeeInvite } from './EmployeeInvite';
import { EmployeePhoto } from './EmployeePhoto';
import { VacationFacts } from './VacationFacts';
import { EmployeePayroll } from './EmployeePayroll';
import { EmployeeFactBox } from './EmployeeFactBox';
import { CardLayout } from '../../components/CardLayout';
import './hr.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const date = (d: string | null) => (d ? dateFormat.format(new Date(`${d}T12:00:00`)) : null);

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children || <span className="muted">–</span>}</dd>
    </div>
  );
}

/** Systemrolle des verknüpften Benutzers ändern. Wer das darf und welche Rollen zur Wahl stehen, regelt die Benutzerverwaltung (der Inhaber nur durch einen Inhaber). */
function SystemRole({ employee, onChanged }: { employee: Employee; onChanged: () => void }) {
  const client = useQueryClient();
  const roles = useQuery({ queryKey: ['roles'], queryFn: async () => (await api<{ data: RoleSummary[] }>('GET', '/users/roles')).data });
  const [error, setError] = useState<string | null>(null);
  const change = useMutation({
    mutationFn: (roleKey: string) => api('PATCH', `/users/${employee.userId}`, { roleKey }),
    onSuccess: () => {
      setError(null);
      client.invalidateQueries({ queryKey: ['users'] });
      client.invalidateQueries({ queryKey: ['employee', employee.id] });
      client.invalidateQueries({ queryKey: ['employees'] });
      onChanged();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Die Rolle konnte nicht geändert werden.'),
  });
  return (
    <>
      <select className="input input--inline" aria-label="Systemrolle ändern" value={employee.userRoleKey ?? ''} disabled={change.isPending || roles.isLoading} onChange={(ev) => change.mutate(ev.target.value)}>
        {(roles.data ?? []).map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        {employee.userRoleKey && !(roles.data ?? []).some((r) => r.key === employee.userRoleKey) && <option value={employee.userRoleKey}>{employee.userRoleName}</option>}
      </select>
      {error && <small className="field__error" role="alert">{error}</small>}
    </>
  );
}

/**
 * Karte eines Mitarbeiters: Stammdaten, sensible Daten (verborgen), Verträge und Verlauf. Deaktivieren und Aktivieren darf, wer bearbeiten darf.
 * Endgültig löschen darf nur ein Inhaber, und nur einen deaktivierten Mitarbeiter (KON-079).
 */
export function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can, hasModule, session } = useSession();
  const [version, setVersion] = useState(0);
  // Der Einladungslink bleibt sichtbar, auch wenn der Mitarbeiter danach einen Zugang hat und der Einladungsbereich verschwindet
  const [invitation, setInvitation] = useState<{ result: InvitationResult; mailRequested: boolean } | null>(null);
  // Der Verlauf lädt neu, wenn auf der Karte etwas geschehen ist
  const bump = () => setVersion((v) => v + 1);
  const isOwner = session?.user.role.key === 'owner';

  const employee = useQuery({ queryKey: ['employee', id], queryFn: async () => (await api<{ data: Employee }>('GET', `/employees/${id}`)).data });
  const setActive = useMutation({
    mutationFn: (isActive: boolean) => api<{ data: Employee }>('PATCH', `/employees/${id}`, { isActive }),
    onSuccess: (res) => {
      client.setQueryData(['employee', id], res.data);
      client.invalidateQueries({ queryKey: ['employees'] });
      bump();
    },
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/employees/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['employees'] });
      client.removeQueries({ queryKey: ['employee', id] });
      navigate('/mitarbeiter', { replace: true });
    },
  });

  if (employee.isLoading) return <p className="muted">Wird geladen …</p>;
  if (employee.isError || !employee.data) {
    return (
      <div className="card narrow">
        <h1>Mitarbeiter nicht gefunden</h1>
        <Link to="/mitarbeiter">Zur Liste</Link>
      </div>
    );
  }
  const e = employee.data;
  const error = [setActive.error, remove.error].find((x) => x instanceof ApiError) as ApiError | undefined;
  const address = [e.street, [e.postalCode, e.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  return (
    <>
      <p><Link to="/mitarbeiter" className="back-link">← Mitarbeiter</Link></p>
      <div className="page-head">
        <h1>{e.firstName} {e.lastName}</h1>
        <span className={`badge ${e.isActive ? 'badge--won' : ''}`}>{e.isActive ? 'Aktiv' : 'Deaktiviert'}</span>
        <span className="muted">Personalnummer {e.personnelNumber}</span>
      </div>
      {error && <div className="alert alert--error" role="alert">{error.message}</div>}
      {invitation && <InvitationLink result={invitation.result} mailRequested={invitation.mailRequested} onClose={() => setInvitation(null)} />}

      <CardLayout factbox={<EmployeeFactBox employee={e} />}>
      <div className="card employee-card">
        <div className="employee-card__main">
        <div className="employee-actions">
          {can('hr.write') && <Link className="btn btn--primary btn--sm" to={`/mitarbeiter/${e.id}/bearbeiten`}>Bearbeiten</Link>}
          {can('hr.write') &&
            (e.isActive ? (
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={setActive.isPending || !!e.deactivationBlocked}
                title={e.deactivationBlocked ?? undefined}
                onClick={() => {
                  if (window.confirm(`${e.firstName} ${e.lastName} deaktivieren? Die Daten bleiben erhalten, der Mitarbeiter erscheint nur noch unter „Deaktiviert“. Einen Benutzerzugang ändert das nicht.`)) setActive.mutate(false);
                }}
              >
                Deaktivieren
              </button>
            ) : (
              <button type="button" className="btn btn--secondary btn--sm" disabled={setActive.isPending} onClick={() => setActive.mutate(true)}>Wieder aktivieren</button>
            ))}
          {isOwner && can('hr.delete') && !e.isActive && (
            <button
              type="button"
              className="btn btn--danger btn--sm"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`${e.firstName} ${e.lastName} endgültig löschen?\n\nDer Datensatz und alle Dateien werden entfernt. Das lässt sich nicht rückgängig machen. Prüfen Sie vorher, ob Aufbewahrungsfristen es zulassen. Der Eintrag im Verlauf bleibt bestehen.`)) remove.mutate();
              }}
            >
              Endgültig löschen
            </button>
          )}
        </div>
        {e.deactivationBlocked && <p className="muted small">{e.deactivationBlocked}</p>}
        {e.isActive && isOwner && <p className="muted small">Einen aktiven Mitarbeiter löschen Sie nicht, sondern deaktivieren ihn. Erst danach lässt er sich endgültig löschen.</p>}

        <dl className="facts">
          <Fact label="Position">{e.position}</Fact>
          <Fact label="Abteilung">{e.department}</Fact>
          <Fact label="Eintritt">{date(e.hiredOn)}</Fact>
          <Fact label="Austritt">{date(e.leftOn)}</Fact>
          <Fact label="E-Mail">{e.email && <a href={`mailto:${e.email}`}>{e.email}</a>}</Fact>
          <Fact label="Telefon">{e.phone && <a href={`tel:${e.phone.replace(/\s+/g, '')}`}>{e.phone}</a>}</Fact>
          <Fact label="Anschrift">{address}</Fact>
          <Fact label="Notfallkontakt">{[e.emergencyName, e.emergencyPhone].filter(Boolean).join(', ')}</Fact>
          <Fact label="Benutzerzugang">{e.userName ? `${e.userName}` : 'Keiner verknüpft'}</Fact>
          {e.userId && <Fact label="Systemrolle">{can('users.write') ? <SystemRole employee={e} onChanged={bump} /> : e.userRoleName}</Fact>}
        </dl>
        {e.vacation && <VacationFacts vacation={e.vacation} />}
        </div>
        <EmployeePhoto employee={e} canWrite={can('hr.write')} onChanged={bump} />
      </div>

      {!e.userId && e.isActive && hasModule('users') && can('hr.write') && can('users.write') && can('users.read') && (
        <EmployeeInvite
          key={e.id}
          employee={e}
          onInvited={(result, mailRequested) => {
            setInvitation({ result, mailRequested });
            client.invalidateQueries({ queryKey: ['employee', id] });
            bump();
          }}
        />
      )}
      <EmployeePayroll employee={e} onChanged={bump} />
      <EmployeeFiles employeeId={e.id} onChanged={bump} />
      <EmployeeHistory employeeId={e.id} version={version} />
      </CardLayout>
    </>
  );
}
